/**
 * Turning a stay into (a) the itinerary days it actually covers and
 * (b) the WhatsApp message Ananya receives.
 *
 * There is no payment gateway and no booking backend beyond the table: the
 * booking row is written, then the guest's WhatsApp opens with the full summary
 * addressed to Ananya, who confirms by hand. See DEPLOY.md.
 */

export const ANANYA_WHATSAPP = '918583079287';

/**
 * Which itinerary days a stay of N nights covers.
 * 2 nights -> arrival + middle + departure. 1 night -> arrival + departure.
 * 3+ nights -> the middle day repeats. 0 nights -> arrival only (a day visit).
 */
export const daysForStay = (allDays, nights, startDate = null) => {
  const sorted = [...(allDays || [])].sort((a, b) => a.dayIndex - b.dayIndex);
  const arrival = sorted.find((d) => d.role === 'ARRIVAL') || sorted[0];
  const departure = sorted.find((d) => d.role === 'DEPARTURE') || sorted[sorted.length - 1];
  const middles = sorted.filter((d) => d.role === 'MIDDLE');

  if (!arrival) return [];

  const out = nights <= 0
    ? [{ ...arrival, stayDayIndex: 1 }]
    : [{ ...arrival, stayDayIndex: 1 }];

  if (nights > 0) {
    for (let i = 0; i < nights - 1; i += 1) {
      const middle = middles[Math.min(i, middles.length - 1)];
      if (middle) out.push({ ...middle, stayDayIndex: out.length + 1, repeatOf: i });
    }
    if (departure && departure.id !== arrival.id) {
      out.push({ ...departure, stayDayIndex: out.length + 1 });
    }
  }

  // A 3-night stay repeats the middle day, so two days would otherwise both read
  // "Day 2 — Saturday" and Ananya cannot tell which night is which. Stamp the
  // real date on each and label from that.
  return out.map((d, i) => {
    const date = startDate ? addDays(startDate, i) : null;
    return {
      ...d,
      date,
      // "Day 2 — Sat, 29 Aug 2026", falling back to the template title
      dayLabel: date ? `Day ${i + 1} — ${prettyDate(date)}` : `Day ${i + 1} — ${d.title}`,
    };
  });
};

/**
 * The stay's meals as a nested day -> meal -> question structure.
 *
 * The booking form used to render `requiredChoices` as one flat list of
 * dropdowns, which on a three-night stay is fourteen questions in a single
 * scroll with no sense of where you are. The UI needs the shape: which meal a
 * question belongs to, what else is served at that meal, and how much of each
 * day is left. `requiredChoices` is now derived from this, so the list the form
 * renders and the list it submits cannot drift apart.
 *
 * Meals with no choices are kept: "Morning tea & fruit — nothing to choose" is
 * information, and dropping it made the day look shorter than it is.
 */
export const mealPlan = (days) => (days || []).map((day) => {
  const meals = [...(day.items?.items || [])]
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .filter((it) => it.kind === 'MEAL')
    .map((item) => ({
      id: item.id,
      timeLabel: item.timeLabel,
      title: item.title,
      body: item.body,
      note: item.note,
      // what arrives whatever you pick -- shown so the choice reads as one part
      // of a meal rather than the whole of it
      fixedItems: item.fixedItems || [],
      groups: [...(item.optionGroups?.items || [])]
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((group) => ({
          key: `${day.stayDayIndex}:${item.id}:${group.id}`,
          stayDayIndex: day.stayDayIndex,
          dayTitle: day.dayLabel || day.title,
          dayDate: day.date || null,
          dayItemId: item.id,
          slotLabel: item.title,
          timeLabel: item.timeLabel,
          group,
          options: [...(group.options?.items || [])]
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
        })),
    }));

  return {
    stayDayIndex: day.stayDayIndex,
    dayLabel: day.dayLabel || day.title,
    subtitle: day.subtitle,
    date: day.date || null,
    meals,
    questionCount: meals.reduce((n, m) => n + m.groups.length, 0),
  };
});

/** Every option group a guest must answer, flattened across the chosen days. */
export const requiredChoices = (days) =>
  mealPlan(days).flatMap((day) => day.meals.flatMap((meal) => meal.groups));

/** How many of a day's questions are answered. */
export const dayProgress = (day, choices = {}) => {
  const groups = day.meals.flatMap((m) => m.groups);
  return {
    total: groups.length,
    answered: groups.filter((g) => choices[g.key]).length,
  };
};

const addDays = (isoDate, n) => {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export const prettyDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '';

export { addDays };

/**
 * Which of the chosen dates are already gone.
 *
 * A stay occupies the nights BETWEEN arrival and departure, so a taken date can
 * still be a legal departure -- you leave the morning the next guest arrives.
 *
 * A day visit has no nights at all, so walking the range finds nothing and the
 * check passes vacuously. It occupies the date itself instead: one booking
 * takes the entire farm, so a day someone else already has is not a day you can
 * visit on. Without the `dayVisit` branch a day visit submitted straight over
 * the top of somebody's stay, silently.
 */
export const dateClashes = ({ startDate, endDate, takenNights = [], dayVisit = false }) => {
  const taken = new Set(takenNights);
  if (dayVisit) return startDate && taken.has(startDate) ? [startDate] : [];
  if (!startDate || !endDate || endDate <= startDate) return [];
  const out = [];
  const cur = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);
  while (cur < end) {
    const d = cur.toISOString().slice(0, 10);
    if (taken.has(d)) out.push(d);
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return out;
};

/**
 * The message that lands in Ananya's WhatsApp. Plain text, because wa.me
 * strips anything cleverer.
 */
/** "Guest 1", or their name when the booker gave us one. */
export const guestLabel = (guestNames, index) => {
  const name = guestNames?.[index];
  return name ? `${name} (guest ${index + 1})` : `Guest ${index + 1}`;
};

export const buildWhatsappMessage = ({
  pkg, bookingNumber, startDate, endDate, nights,
  adults, childAges, quote, guest, choices, choiceMeta, surpriseUs = false,
  deposit = null, dayVisitTier = null,
}) => {
  const L = [];
  L.push(`*New farm stay request*${bookingNumber ? ` — ${bookingNumber}` : ''}`);
  L.push(pkg?.name || 'Farm stay');
  L.push('');
  L.push(`*Dates:* ${prettyDate(startDate)}${nights > 0 ? ` → ${prettyDate(endDate)}` : ' (day visit)'}`);
  if (nights > 0) L.push(`*Nights:* ${nights}`);

  if (dayVisitTier) L.push(`*Day visit:* ${dayVisitTier.label}`);

  const kids = childAges.length
    ? `, ${childAges.length} ${childAges.length === 1 ? 'child' : 'children'} (age ${childAges.join(', ')})`
    : '';
  L.push(`*Guests:* ${adults} ${adults === 1 ? 'adult' : 'adults'}${kids}`);

  if (quote?.total != null) {
    L.push('');
    L.push('*Estimate*');
    quote.lines.forEach((l) => {
      L.push(`• ${l.label}${l.detail ? ` (${l.detail})` : ''}: ${l.free ? 'free' : `₹${l.amount.toLocaleString('en-IN')}`}`);
    });
    L.push(`*Total: ₹${quote.total.toLocaleString('en-IN')}*`);
    // Both halves, because the guest asking "how much now?" and Ananya
    // answering from a different screen is how a booking goes wrong.
    if (deposit && quote.total > 0) {
      L.push(`Advance to confirm (${deposit.percent}%): ₹${deposit.dueNow.toLocaleString('en-IN')}`);
      L.push(`${dayVisitTier ? 'On the day' : 'At checkout'}: ₹${deposit.dueAtCheckout.toLocaleString('en-IN')}`);
    }
  }

  L.push('');
  L.push('*Guest*');
  L.push(`${guest.name}`);
  L.push(`${guest.phone}`);
  if (guest.email) L.push(`${guest.email}`);

  // Group by stayDayIndex, NOT by day title: a 3-night stay repeats the middle
  // itinerary day, so grouping by title silently merged two nights into one
  // block and Ananya could not tell which night was which.
  const byDay = new Map();
  choiceMeta.forEach((c) => {
    if (!choices[c.key]) return;
    if (!byDay.has(c.stayDayIndex)) byDay.set(c.stayDayIndex, { label: c.dayTitle, entries: [] });
    byDay.get(c.stayDayIndex).entries.push(c);
  });

  if (surpriseUs) {
    L.push('');
    L.push('*Food choices*');
    L.push('Surprise us — the kitchen chooses every meal.');
  } else if (byDay.size) {
    L.push('');
    L.push('*Food choices*');
    [...byDay.entries()].sort((a, b) => a[0] - b[0]).forEach(([, { label, entries }]) => {
      L.push(`_${label}_`);
      entries.forEach((c) => {
        const picked = choices[c.key];
        const opts = c.group.options?.items || [];
        const nameOf = (id) => opts.find((o) => o.id === id)?.label || '—';

        // One choice per course for the whole family -- there is no per-guest
        // selection any more, so this is always a single dish.
        L.push(`• ${c.slotLabel} — ${c.group.label}: ${nameOf(picked)}`);
      });
    });
  }

  if (guest.dietaryNotes) {
    L.push('');
    L.push(`*Notes:* ${guest.dietaryNotes}`);
  }

  L.push('');
  L.push('_Sent from vidhifarm.in_');
  return L.join('\n');
};

export const whatsappUrl = (message) =>
  `https://wa.me/${ANANYA_WHATSAPP}?text=${encodeURIComponent(message)}`;
